import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AutomationsEmployeesService,
  EmployeeWorkspaceType,
  type EmployeeListByWorkspaceBodyParams,
} from 'src/api/generated';
import { MINUTE_IN_MS } from 'src/utils/date';

interface AiWorkspaceStatusIdsResponse {
  aiStatusIds: number[];
  loading: boolean;
  error: Error | null;
}

export const useAiWorkspaceStatusIdsByStatus = (
  statusIds: number[],
  enabled = true
): AiWorkspaceStatusIdsResponse => {
  const uniqueStatusIds = useMemo(
    () => Array.from(new Set(statusIds)).sort((a, b) => a - b),
    [statusIds]
  );

  const queryResult = useQuery({
    queryKey: [
      'automations/employees/workspaces/list-by-workspace',
      EmployeeWorkspaceType.FUNNEL_STATUS,
      uniqueStatusIds,
    ],
    queryFn: async () => {
      const requestBody: EmployeeListByWorkspaceBodyParams = {
        workspaceIds: uniqueStatusIds.map((statusId) => statusId.toString()),
      };

      return AutomationsEmployeesService.listEmployeesByWorkspace(
        EmployeeWorkspaceType.FUNNEL_STATUS,
        requestBody
      );
    },
    enabled: enabled && uniqueStatusIds.length > 0,
    staleTime: MINUTE_IN_MS * 15,
  });

  const aiStatusIds = useMemo(
    () =>
      Array.from(
        new Set(
          (queryResult.data ?? [])
            .map((workspace) => Number(workspace.workspaceId))
            .filter((statusId) => !Number.isNaN(statusId))
        )
      ).sort((a, b) => a - b),
    [queryResult.data]
  );

  return {
    aiStatusIds,
    loading: queryResult.isLoading,
    error: queryResult.error ?? null,
  };
};
